import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

export default function InventoryReservations() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/reservations?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  const fetchAux = useCallback(async () => {
    const [pRes, wRes] = await Promise.all([
      fetch(`${API}/companies/${companyId}/inventory/products?per_page=200`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
    ]);
    const pJson = await pRes.json();
    setProducts(Array.isArray(pJson.data) ? pJson.data : []);
    setWarehouses(await wRes.json());
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const release = async (id) => {
    if (!window.confirm('Release this reservation?')) return;
    await fetch(`${API}/companies/${companyId}/inventory/reservations/${id}/release`, { method: 'POST', headers: getAuthHeaders() });
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🔖 Reservations & Stock Allocation</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ New Reservation</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="partial">Partial</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="released">Released</option>
          <option value="shortage">Shortage</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Product', 'Warehouse', 'Order #', 'Qty', 'Fulfilled', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No reservations</td></tr>}
              {list.map(r => (
                <tr key={r.id}>
                  <td style={tdStyle}>{r.product?.name}</td>
                  <td style={tdStyle}>{r.warehouse?.name}</td>
                  <td style={tdStyle}>{r.order_number || '—'}</td>
                  <td style={tdStyle}>{fmtQty(r.quantity)}</td>
                  <td style={tdStyle}>{fmtQty(r.fulfilled_qty)}</td>
                  <td style={tdStyle}><Badge value={r.status} /></td>
                  <td style={tdStyle}>
                    {['active', 'partial', 'shortage'].includes(r.status) && (
                      <button style={btnSmall} onClick={() => release(r.id)}>Release</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <ReservationModal companyId={companyId} products={products} warehouses={warehouses}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
    </div>
  );
}

function ReservationModal({ companyId, products, warehouses, onClose, onSaved }) {
  const [form, setForm] = useState({ product_id: '', warehouse_id: '', quantity: '', order_number: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.product_id || !form.warehouse_id || !form.quantity) { alert('Product, warehouse, and quantity are required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/reservations`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Reservation</h3>
        <FormRow label="Product *">
          <select style={inputStyle} value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })}>
            <option value="">— Select —</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Warehouse *">
          <select style={inputStyle} value={form.warehouse_id} onChange={e => setForm({ ...form, warehouse_id: e.target.value })}>
            <option value="">— Select —</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Quantity *"><input type="number" style={inputStyle} value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></FormRow>
        <FormRow label="Order # (reference)"><input style={inputStyle} value={form.order_number} onChange={e => setForm({ ...form, order_number: e.target.value })} /></FormRow>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Reserve'}</button>
        </div>
      </div>
    </div>
  );
}
